'use client'

import classnames from 'classnames'
import styles from './Locations3UpOpenRoles.module.scss'
import Locations3UpOpenRolesCard from '@/sections/Locations3UpOpenRoles/Locations3UpOpenRolesCard/Locations3UpOpenRolesCard'
import 'swiper/css'
import Swiper from 'swiper'
import { SwiperOptions } from 'swiper/types'
import { useEffect, useRef, useState } from 'react'
import useWindowResize from '@/hooks/use-window-resize'
import useBreakpoint from '@/hooks/use-breakpoint'
import Icon from '@/components/Icon/Icon'

const Locations3UpOpenRoles = ({ className, title, items }: SanityLocations3UpOpenRoles) => {
  const carousel = useRef<Swiper | null>(null)
  const carouselRef = useRef<HTMLDivElement | null>(null)
  const resizeKey = useWindowResize()
  const [activeIndex, setActiveIndex] = useState(0)
  const [isInitialized, setIsInitialized] = useState(false)
  const { isMobile } = useBreakpoint()

  const shouldCarousel =
    (isMobile === true && items?.length > 1) ||
    (isMobile === false && items?.length >= 4)

  const isFirst = activeIndex === 0
  const isLast = activeIndex === (items?.length ?? 1) - 1

  const goToPrev = () => {
    if (!isFirst) setActiveIndex(activeIndex - 1)
  }

  const goToNext = () => {
    if (!isLast) setActiveIndex(activeIndex + 1)
  }

  useEffect(() => {
    if (!carousel.current || !isInitialized) return
    carousel?.current?.slideTo(activeIndex)
  }, [activeIndex, isInitialized])

  useEffect(() => {
    if (!shouldCarousel || !carouselRef.current) return

    if (carousel.current) {
      carousel.current.destroy()
      setIsInitialized(false)
    }

    const settings: SwiperOptions = {
      slidesPerView: 'auto',
      spaceBetween: 20,
      on: {
        init: function () {
          setIsInitialized(true)
        },
      },
    }

    carousel.current = new Swiper(carouselRef.current, settings)

    carousel.current.on('slideChange', function () {
      if (!carousel.current) return
      setActiveIndex(carousel.current?.activeIndex)
    })

    return () => {
      if (carousel.current) {
        carousel.current.destroy()
        carousel.current = null
      }
    }
  }, [resizeKey, shouldCarousel])

  if (!title || !items?.length) return null

  return (
    <div className={classnames(styles.Locations3UpOpenRoles, className)}>
      <div className={styles.inner}>
        <div className={styles.header}>
          <h1 className={styles.title}>{title}</h1>
          {shouldCarousel && (
            <div className={styles.navArrows}>
              <button
                className={classnames(styles.navArrow, styles.navArrowPrev, {
                  [styles.navArrowDisabled]: isFirst,
                })}
                onClick={goToPrev}
                disabled={isFirst}
                aria-label="Previous slide"
              >
                <Icon name="caretRight" className={styles.navArrowIcon} />
              </button>
              <button
                className={classnames(styles.navArrow, styles.navArrowNext, {
                  [styles.navArrowDisabled]: isLast,
                })}
                onClick={goToNext}
                disabled={isLast}
                aria-label="Next slide"
              >
                <Icon name="caretRight" className={styles.navArrowIcon} />
              </button>
            </div>
          )}
        </div>
        {shouldCarousel ? (
          <>
            <div
              className={styles.carouselContainer}
              ref={carouselRef}
            >
              <ul className={classnames('swiper-wrapper', styles.listWrapper)}>
                {items.map((item, i) => (
                  <li
                    key={i}
                    className={classnames('swiper-slide', styles.listWrapper__item)}
                  >
                    <Locations3UpOpenRolesCard
                      {...item}
                      className={styles.item}
                    />
                  </li>
                ))}
              </ul>
            </div>
          </>
        ) : (
          <div
            className={classnames(styles.items, {
              [styles.itemsCount1]: items.length === 1,
              [styles.itemsCount2]: items.length === 2,
              [styles.itemsCount3]: items.length >= 3,
            })}
          >
            {items.map((item, i) => (
              <Locations3UpOpenRolesCard
                key={i}
                {...item}
                className={styles.item}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

Locations3UpOpenRoles.displayName = 'Locations3UpOpenRoles'

export default Locations3UpOpenRoles
